'use client';

import { useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { Card, CardContent } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import OptimizedImage from '@/components/optimized-media/OptimizedImage';
import { ChevronLeft, ChevronRight, Star, Quote, MapPin, Calendar, Smile } from 'lucide-react';
import { useTranslation } from "@/hooks/useTranslation";
import { convertToLocaleNumber } from "@/lib/i18n/numbers";
import { ensureStringArray } from "@/lib/utils";


export default function TestimonialsSection() {
    const { t, locale, isRTL } = useTranslation("all-on-4");
    const [currentTestimonial, setCurrentTestimonial] = useState(0);

    const testimonials = [
        {
            id: 1,
            name: t("testimonials.patients.sarah.name"),
            age: convertToLocaleNumber(58, locale),
            country: t("testimonials.patients.sarah.country"),
            treatment: t("testimonials.patients.sarah.treatment"),
            date: t("testimonials.patients.sarah.date"),
            rating: 5,
            story: t("testimonials.patients.sarah.story"),
            highlights: ensureStringArray(t("testimonials.patients.sarah.highlights", { returnObjects: true })),
            beforeImage: '/images/treatments/all-on-4/sarah-mitchell-before.webp',
            afterImage: '/images/treatments/all-on-4/sarah-mitchell-after.webp',
            portrait: '/images/treatments/all-on-4/sarah-mitchell-portrait.webp'
        }
    ];

    const stats = [
        {
            number: t("testimonials.stats.successRate.number"),
            label: t("testimonials.stats.successRate.label"),
            description: t("testimonials.stats.successRate.description")
        },
        {
            number: t("testimonials.stats.happyPatients.number"),
            label: t("testimonials.stats.happyPatients.label"),
            description: t("testimonials.stats.happyPatients.description")
        },
        {
            number: t("testimonials.stats.experience.number"),
            label: t("testimonials.stats.experience.label"),
            description: t("testimonials.stats.experience.description")
        },
        {
            number: t("testimonials.stats.countries.number"),
            label: t("testimonials.stats.countries.label"),
            description: t("testimonials.stats.countries.description")
        }
    ];

    const nextTestimonial = () => {
        setCurrentTestimonial((prev) => (prev + 1) % testimonials.length);
    };

    const prevTestimonial = () => {
        setCurrentTestimonial((prev) => (prev - 1 + testimonials.length) % testimonials.length);
    };

    const currentData = testimonials[currentTestimonial];

    return (
        <section id="testimonials" className="py-16 lg:py-24 bg-white dark:bg-slate-800 relative overflow-hidden">
            {/* Background decoration */}
            <div className="absolute inset-0 opacity-5">
                <div className="absolute top-20 left-10 w-64 h-64 bg-gold rounded-full blur-3xl"></div>
                <div className="absolute bottom-20 right-10 w-80 h-80 bg-gold rounded-full blur-2xl"></div>
            </div>

            <div className="container relative">
                {/* Section Header */}
                <div className="text-center mb-16">
                    <h2 className="text-3xl md:text-4xl lg:text-5xl font-bold text-navy dark:text-white font-serif leading-tight mb-6">
                        {(() => {
                            const title = t("testimonials.title");
                            const word = t("testimonials.highlightWord");
                            if (word && title.includes(word)) {
                                const idx = title.indexOf(word);
                                return (
                                    <>
                                        {title.slice(0, idx)}
                                        <span className="text-gold">{word}</span>
                                        {title.slice(idx + word.length)}
                                    </>
                                );
                            }
                            return title;
                        })()}
                    </h2>
                    <p className="text-lg text-slate-600 dark:text-slate-300 max-w-3xl mx-auto">
                        {t("testimonials.subtitle")}
                    </p>
                </div>

                {/* Stats Row */}
                <div className="grid grid-cols-2 md:grid-cols-4 gap-8 mb-16">
                    {stats.map((stat, index) => (
                        <motion.div
                            key={index}
                            initial={{ opacity: 0, y: 30 }}
                            whileInView={{ opacity: 1, y: 0 }}
                            transition={{ delay: index * 0.1, duration: 0.5 }}
                            viewport={{ once: true }}
                            className="text-center"
                        >
                            <div className="text-3xl lg:text-4xl font-bold text-gold mb-2">
                                {stat.number}
                            </div>
                            <div className="text-lg font-semibold text-navy dark:text-white mb-1">
                                {stat.label}
                            </div>
                            <div className="text-sm text-slate-600 dark:text-slate-300">
                                {stat.description}
                            </div>
                        </motion.div>
                    ))}
                </div>

                {/* Main Testimonial */}
                <div className="max-w-6xl mx-auto">
                    <Card className="border-slate-200 dark:border-slate-700 overflow-hidden lg:h-[600px]">
                        <CardContent className="p-0 h-full">
                            <div className="grid lg:grid-cols-2 h-full">
                                {/* Patient Story */}
                                <div className={`p-8 lg:p-12 lg:min-h-[600px] flex flex-col ${isRTL ? 'text-right' : ''}`}>
                                    <div className={`flex items-center mb-6 ${isRTL ? 'justify-end' : ''}`}>
                                        <div className={`relative w-16 h-16 bg-gold/20 rounded-full flex items-center justify-center overflow-hidden ${isRTL ? 'ml-4' : 'mr-4'}`}>
                                            {currentData.portrait ? (
                                                <OptimizedImage
                                                    src={currentData.portrait}
                                                    alt={currentData.name}
                                                    fill
                                                    sizes="64px"
                                                />
                                            ) : (
                                                <Quote className="w-8 h-8 text-gold" />
                                            )}
                                        </div>
                                        <div className={`flex-1 ${isRTL ? 'text-right' : ''}`}>
                                            <h3 className={`text-xl font-bold text-navy dark:text-white ${isRTL ? 'text-right' : ''}`}>
                                                {currentData.name}
                                            </h3>
                                            <div className={`flex items-center text-sm text-slate-600 dark:text-slate-300 gap-1 ${isRTL ? 'flex-row-reverse justify-end' : ''}`}>
                                                <MapPin className="w-4 h-4" />
                                                {currentData.country}
                                                <span className="mx-2">•</span>
                                                <Calendar className="w-4 h-4" />
                                                {currentData.date}
                                            </div>
                                        </div>
                                    </div>

                                    <div className={`flex items-center mb-4 gap-3 ${isRTL ? 'flex-row-reverse justify-end' : ''}`}>
                                        <div className={`flex ${isRTL ? 'flex-row-reverse' : ''}`}>
                                            {[...Array(5)].map((_, i) => (
                                                <Star
                                                    key={i}
                                                    className={`w-5 h-5 ${i < currentData.rating
                                                        ? 'text-gold fill-current'
                                                        : 'text-gray-300'
                                                        }`}
                                                />
                                            ))}
                                        </div>
                                        <span className="text-sm font-medium text-gold">
                                            {currentData.treatment}
                                        </span>
                                    </div>

                                    <div className="flex-1 flex flex-col">
                                        <blockquote className={`text-lg text-slate-600 dark:text-slate-300 leading-relaxed mb-6 min-h-[120px] ${isRTL ? 'text-right' : ''}`}>
                                            "{currentData.story}"
                                        </blockquote>

                                        <div className="space-y-2 mb-8 flex-1">
                                            <h4 className={`font-semibold text-navy dark:text-white mb-3 ${isRTL ? 'text-right' : 'text-left'}`}>
                                                {t("testimonials.keyBenefits")}:
                                            </h4>
                                            <div className="grid grid-cols-1 gap-2">
                                                {currentData.highlights.map((highlight, index) => (
                                                    <div key={index} className="flex items-center gap-2">
                                                        <Smile className="w-4 h-4 text-gold shrink-0" />
                                                        <span className={`text-slate-600 dark:text-slate-300 text-sm ${isRTL ? 'text-right' : ''}`}>
                                                            {highlight}
                                                        </span>
                                                    </div>
                                                ))}
                                            </div>
                                        </div>

                                        {testimonials.length > 1 && (
                                            <div className={`flex items-center mt-auto justify-end`}>
                                                <div className={`flex ${isRTL ? 'space-x-reverse space-x-2' : 'space-x-2'}`}>
                                                    <Button
                                                        onClick={prevTestimonial}
                                                        variant="outline"
                                                        size="sm"
                                                        aria-label={t("testimonials.previous") || "Previous"}
                                                        className="border-gold text-gold hover:bg-gold hover:text-navy"
                                                    >
                                                        {isRTL ? <ChevronRight className="w-4 h-4" /> : <ChevronLeft className="w-4 h-4" />}
                                                    </Button>
                                                    <Button
                                                        onClick={nextTestimonial}
                                                        variant="outline"
                                                        size="sm"
                                                        aria-label={t("testimonials.next") || "Next"}
                                                        className="border-gold text-gold hover:bg-gold hover:text-navy"
                                                    >
                                                        {isRTL ? <ChevronLeft className="w-4 h-4" /> : <ChevronRight className="w-4 h-4" />}
                                                    </Button>
                                                </div>
                                            </div>
                                        )}
                                    </div>
                                </div>

                                {/* Before/After Images */}
                                <div className="bg-slate-50 dark:bg-navy-950 p-8 lg:p-12 lg:min-h-[600px] flex items-center">
                                    <div className="w-full">
                                        <AnimatePresence mode="wait">
                                            <motion.div
                                                key={`beforeafter-${currentData.id}`}
                                                initial={{ opacity: 0, scale: 0.95 }}
                                                animate={{ opacity: 1, scale: 1 }}
                                                exit={{ opacity: 0, scale: 0.95 }}
                                                transition={{ duration: 0.3 }}
                                                className="space-y-6"
                                            >
                                                <h4 className="text-xl font-bold text-navy dark:text-white text-center mb-6">
                                                    {t("testimonials.transformationResults")}
                                                </h4>
                                                <div className="grid grid-cols-2 gap-4">
                                                    <div className="text-center">
                                                        <div className="relative aspect-square rounded-lg overflow-hidden mb-3">
                                                            <OptimizedImage
                                                                src={currentData.beforeImage}
                                                                alt={`${currentData.name} - ${t("testimonials.before")}`}
                                                                fill
                                                                sizes="(max-width: 1024px) 50vw, 25vw"
                                                            />
                                                            <div className="absolute bottom-2 left-2 bg-black/70 text-white px-2 py-1 rounded text-sm z-10">
                                                                {t("testimonials.before")}
                                                            </div>
                                                        </div>
                                                    </div>
                                                    <div className="text-center">
                                                        <div className="relative aspect-square rounded-lg overflow-hidden mb-3">
                                                            <OptimizedImage
                                                                src={currentData.afterImage}
                                                                alt={`${currentData.name} - ${t("testimonials.after")}`}
                                                                fill
                                                                sizes="(max-width: 1024px) 50vw, 25vw"
                                                            />
                                                            <div className="absolute bottom-2 left-2 bg-gold text-navy px-2 py-1 rounded text-sm font-medium z-10">
                                                                {t("testimonials.after")}
                                                            </div>
                                                        </div>
                                                    </div>
                                                </div>
                                                <div className="text-center">
                                                    <p className="text-sm text-slate-600 dark:text-slate-300">
                                                        {t("testimonials.treatmentCompleted", { date: currentData.date })}
                                                    </p>
                                                </div>
                                            </motion.div>
                                        </AnimatePresence>
                                    </div>
                                </div>
                            </div>
                        </CardContent>
                    </Card>

                    {/* Navigation Dots — only when there is more than one testimonial */}
                    {testimonials.length > 1 && (
                        <div className={`flex justify-center mt-8 ${isRTL ? 'space-x-reverse space-x-2' : 'space-x-2'}`}>
                            {testimonials.map((_, index) => (
                                <button
                                    key={index}
                                    onClick={() => setCurrentTestimonial(index)}
                                    className={`w-3 h-3 rounded-full transition-all duration-300 ${index === currentTestimonial
                                        ? 'bg-gold scale-125'
                                        : 'bg-slate-200 dark:bg-slate-700 hover:bg-gold/50'
                                        }`}
                                />
                            ))}
                        </div>
                    )}
                </div>

                {/* Call to Action */}
                <div className="text-center mt-16">
                    <h3 className="text-2xl font-bold text-navy dark:text-white mb-4">
                        {t("testimonials.cta.title")}
                    </h3>
                    <p className="text-slate-600 dark:text-slate-300 mb-6 max-w-2xl mx-auto">
                        {t("testimonials.cta.description")}
                    </p>
                    <Button
                        onClick={() => {
                            const bookingSection = document.getElementById('booking');
                            bookingSection?.scrollIntoView({ behavior: 'smooth' });
                        }}
                        className="bg-gold hover:bg-gold-600 text-navy font-semibold px-8 py-3 rounded-lg text-lg"
                    >
                        {t("testimonials.cta.button")}
                    </Button>
                </div>
            </div>
        </section>
    );
} 